import type { YinshState } from "@cloudnest/yinsh-engine";

/**
 * Portable, lossless export of a finished YINSH game. The relay event log is
 * the authoritative payload — the offline training converter
 * (`packages/yinsh-ai-core/scripts/convert-human-games.ts`) re-derives every
 * board state, the per-ply action indices, and the winner by replaying these
 * events through the engine. The `players`/`result` block is human-facing
 * metadata only; nothing in it is required to reconstruct the game.
 */
export interface YinshGameExportEvent {
  readonly sequenceNumber: number;
  readonly eventType: string;
  readonly userId: string;
  readonly payload: Record<string, unknown>;
}

export interface YinshGameExportPlayer {
  /** Relay userId, best-effort. The converter matches on `name`, not `id`. */
  readonly id?: string;
  readonly name: string;
  readonly color: "WHITE" | "BLACK";
}

export interface YinshGameExport {
  readonly schemaVersion: 1;
  readonly gameType: "yinsh";
  readonly gameId: string;
  readonly capturedAt: string;
  readonly source: "client_human_game";
  readonly players: readonly YinshGameExportPlayer[];
  readonly result: {
    readonly winner: "WHITE" | "BLACK" | null;
    readonly isDraw: boolean;
    readonly finalScores: { readonly WHITE: number; readonly BLACK: number };
    readonly turn: number;
  };
  readonly events: readonly YinshGameExportEvent[];
}

export function buildYinshGameExport(params: {
  readonly gameId: string;
  readonly capturedAt: string;
  readonly events: readonly YinshGameExportEvent[];
  readonly finalState: YinshState;
}): YinshGameExport {
  const { gameId, capturedAt, events, finalState } = params;
  const [white, black] = finalState.players;

  const idForColor = (color: "WHITE" | "BLACK"): string | undefined =>
    events.find(
      (event) =>
        (event.payload as { player?: string }).player === color &&
        event.userId.length > 0,
    )?.userId;

  return {
    schemaVersion: 1,
    gameType: "yinsh",
    gameId,
    capturedAt,
    source: "client_human_game",
    players: [
      { id: idForColor("WHITE"), name: white?.name ?? "WHITE", color: "WHITE" },
      { id: idForColor("BLACK"), name: black?.name ?? "BLACK", color: "BLACK" },
    ],
    result: {
      winner: finalState.winner,
      isDraw: finalState.isDraw,
      finalScores: {
        WHITE: white?.ringsScored ?? 0,
        BLACK: black?.ringsScored ?? 0,
      },
      turn: finalState.turn,
    },
    events: events.map((event) => ({
      sequenceNumber: event.sequenceNumber,
      eventType: event.eventType,
      userId: event.userId,
      payload: event.payload,
    })),
  };
}

/** Filesystem-safe filename for an export (ISO colons/dots → dashes). */
export function yinshGameExportFilename(exportData: YinshGameExport): string {
  const stamp = exportData.capturedAt.replace(/[:.]/g, "-");
  return `yinsh-${exportData.gameId}-${stamp}.json`;
}
